import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetMaturityEvaluation } from "@/hooks/api/query/useGetMaturityEvaluation";
import { useGetMaturityFramework } from "@/hooks/api/query/useGetMaturityFramework";
import { useResetMaturityFull } from "@/hooks/api/mutate/useResetMaturityFull";
import { SummaryView } from "@/components/maturity/SummaryView";
import { PreliminaryView } from "@/components/maturity/PreliminaryView";
import { FullEvalView } from "@/components/maturity/FullEvalView";
import { AssistedView } from "@/components/maturity/AssistedView";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ProgressIndicator } from "@/components/ui/progress-indicator";
import { MATURITY_STEPS } from "@/constants/steps";
import { AlertTriangle } from "lucide-react";
import type { PageView } from "@/types/maturity";

function EvaluareMaturitate() {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = (searchParams.get("view") as PageView) || "summary";

  const setPage = useCallback(
    (view: PageView) => {
      if (view === "summary") {
        setSearchParams({});
      } else {
        setSearchParams({ view });
      }
    },
    [setSearchParams]
  );

  const { currentSessionId } = useSessionSwitcher();
  const {
    data: evaluation,
    isLoading: isLoadingEval,
    isError: isErrorEval,
  } = useGetMaturityEvaluation(currentSessionId ?? undefined);
  const {
    data: framework,
    isLoading: isLoadingFramework,
    isError: isErrorFramework,
  } = useGetMaturityFramework();
  const resetFullEval = useResetMaturityFull(currentSessionId ?? "");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const isLoading = isLoadingEval || isLoadingFramework;

  const currentStepIndex = useMemo(() => {
    if (page === "preliminary") return 0;
    if (page === "full-eval" || page === "assisted") return 2;
    const state = evaluation?.eval_state;
    if (state === "PRELIM_COMPLETE") return 1;
    if (state === "FULL_COMPLETE") return 3;
    return 0;
  }, [page, evaluation?.eval_state]);

  const handleStepClick = useCallback(
    (stepIndex: number) => {
      if (stepIndex === 0) setPage("preliminary");
      else if (stepIndex === 1 || stepIndex === 3) setPage("summary");
      else if (stepIndex === 2) setPage("full-eval");
    },
    [setPage]
  );

  const content = useMemo(() => {
    if (isLoading || !evaluation || !framework) return null;

    switch (page) {
      case "summary":
        return (
          <SummaryView
            evaluation={evaluation}
            framework={framework}
            onNavigate={setPage}
            onResetFullEval={async () => {
              await resetFullEval.mutateAsync();
              setPage("preliminary");
            }}
            isResetting={resetFullEval.isPending}
          />
        );
      case "preliminary":
        return (
          <PreliminaryView
            evaluation={evaluation}
            framework={framework}
            onNavigate={setPage}
          />
        );
      case "full-eval":
        return (
          <FullEvalView
            evaluation={evaluation}
            framework={framework}
            onNavigate={setPage}
          />
        );
      case "assisted":
        return <AssistedView onNavigate={setPage} />;
      default:
        return null;
    }
  }, [page, isLoading, evaluation, framework, setPage, resetFullEval]);

  if (!currentSessionId) {
    return (
      <div className="max-w-[1120px] mx-auto p-8 text-center text-slate-500">
        <p>
          Selectează sau creează o sesiune pentru a accesa evaluarea de
          maturitate.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="max-w-[1120px] mx-auto space-y-6 p-8">
        <div className="flex items-center gap-3">
          <Skeleton className="w-10 h-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="w-48 h-6" />
            <Skeleton className="w-64 h-4" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
          <Skeleton className="h-96 rounded-xl" />
          <div className="space-y-4">
            <Skeleton className="h-44 rounded-xl" />
            <Skeleton className="h-44 rounded-xl" />
          </div>
        </div>
        <Skeleton className="h-24 rounded-xl" />
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  if (isErrorEval || isErrorFramework || !evaluation || !framework) {
    return (
      <div className="max-w-[1120px] mx-auto p-8 text-center space-y-4">
        <AlertTriangle className="w-10 h-10 text-red-400 mx-auto" />
        <p className="text-slate-600 font-medium">
          Nu s-au putut încărca datele evaluării.
        </p>
        <Button onClick={() => window.location.reload()}>Reîncearcă</Button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="max-w-[1120px] mx-auto mb-6">
        <ProgressIndicator
          steps={MATURITY_STEPS}
          currentStep={currentStepIndex}
          onStepClick={handleStepClick}
          firstStepClickable
        />
      </div>
      {content}
    </div>
  );
}

export default EvaluareMaturitate;
